import { ParentProps, createContext, useContext } from "solid-js";
import { createStore } from "solid-js/store";
import { BOPropertyInfo, BusinessObjectInfo, ExtractData, ExtractDataDetail } from "st-app/api";
import { extractDataEditor as api } from 'st-app/api'

const ExtractDataEditorContext = createContext<ReturnType<typeof buildContext>>()

interface ExtractDataEditorState {
  loadingBoids: string[],
  businessObjects: Record<string, BusinessObjectInfo>
  propertyPicker: {
    selectedBoid: string
    businessObjects: BusinessObjectInfo[]
    additionalBusinessObjects: BusinessObjectInfo[]
    isAdding: boolean
    loading: boolean
  }
}

const buildContext = () => {
  const [state, setState] = createStore<ExtractDataEditorState>({
    loadingBoids: [],
    businessObjects: {},
    propertyPicker: {
      selectedBoid: '',
      businessObjects: [],
      additionalBusinessObjects: [],
      isAdding: false,
      loading: false,
    }
  })

  const setSelectedPropertyPickerBoid = (boid: string) => {
    setState('propertyPicker', 'selectedBoid', boid)
  }

  const loadPropertyPickerBos = async (detail: ExtractDataDetail) => {
    setState('propertyPicker', 'loading', true)
    const boids = [...new Set(detail.items.map(i => i.srcObject!))]
    const boInfos = await loadBusinessObjects(boids)
    setState('propertyPicker', 'businessObjects', boInfos)
    setState('propertyPicker', 'loading', false)
  }

  const addPropertyPickerBo = (bo: BusinessObjectInfo) => {
    loadBusinessObjects([bo.boid!])
    setState('propertyPicker', 'additionalBusinessObjects', (objs) => [...objs, bo])
    setSelectedPropertyPickerBoid(bo.boid!)
    toggleAddOneToOneModal(false)
  }

  const removePropertyPickerBo = (bo: BusinessObjectInfo) => {
    const allBos = [...state.propertyPicker.businessObjects, ...state.propertyPicker.additionalBusinessObjects]
    const currentIdx = allBos.indexOf(bo)
    const prevBo = allBos[currentIdx - 1]


    if (state.propertyPicker.additionalBusinessObjects.includes(bo)) {
      setState('propertyPicker', 'additionalBusinessObjects', (bos) => [
        ...bos.slice(0, bos.indexOf(bo)),
        ...bos.slice(bos.indexOf(bo) + 1),
      ])

      if (prevBo) {
        setState('propertyPicker', 'selectedBoid', prevBo.boid!)
      }
    }
  }

  const resetAdditionalPropertyPickerBos = () => {
    setState('propertyPicker', 'additionalBusinessObjects', [])
  }

  const toggleAddOneToOneModal = (value?: boolean) => setState('propertyPicker', 'isAdding', value ?? !state.propertyPicker.isAdding)

  const loadBusinessObjects = async (boids: string[]): Promise<BusinessObjectInfo[]> => {
    const res: BusinessObjectInfo[] = []
    const boidsToLoad = []
    for (const boid of boids) {
      if (state.businessObjects[boid]) {
        res.push(state.businessObjects[boid])
      } else {
        boidsToLoad.push(boid)
      }
    }

    if (boidsToLoad.length) {
      const newBos = await api.GetBusinessObjectInfos(boidsToLoad);
      const newBosObj: Record<string, BusinessObjectInfo> = {}
  
      for (const newBo of newBos) {
        res.push(newBo);
        newBosObj[newBo.boid!] = newBo
      }
      setState('businessObjects', { ...state.businessObjects, ...newBosObj })
    }

    return res
  }

  const getBusinessObject = async (boid: string): Promise<BusinessObjectInfo> => {
    const [bo] = await loadBusinessObjects([boid])
    return bo
  }

  const getProperties = async (boid: string): Promise<BOPropertyInfo[]> => {
    const bo = await getBusinessObject(boid)
    return bo.properties
  }

  return [
    state,
    {
      getProperties,
      loadBusinessObjects,
      getBusinessObject,
      setSelectedPropertyPickerBoid,
      loadPropertyPickerBos,
      addPropertyPickerBo,
      removePropertyPickerBo,
      toggleAddOneToOneModal,
      resetAdditionalPropertyPickerBos,
    }
  ] as const
}


export function ExtractDataEditorProvider(props: ParentProps) {
  const ctx = buildContext()

  return (
    <ExtractDataEditorContext.Provider value={ctx}>
      {props.children}
    </ExtractDataEditorContext.Provider>
  )
}

export function useExtractDataEditor() { return useContext(ExtractDataEditorContext) }

